// =============================================================================
// Origin UI — src/services/evaluationPdfGenerator.ts
// Printable A4 PDF of a single evaluation form (completed or in progress).
// Used by My Evaluation (employee) and the manager Evaluation form page.
// Visibility mirrors the on-screen rules: an employee does not see manager
// sections or the overall rating until the manager has completed the review.
// =============================================================================

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { EvaluationDetail, SectionWithResponses, QuestionWithResponse } from './evaluationApi';

// ─── Brand colours ───
const NAVY: [number, number, number] = [10, 34, 64];        // #0A2240
const ORANGE: [number, number, number] = [255, 131, 0];     // #FF8300 — manager sections
const TURQUOISE: [number, number, number] = [0, 176, 185];  // #00B0B9 — employee sections
const WHITE: [number, number, number] = [255, 255, 255];
const MID_GREY: [number, number, number] = [108, 117, 125];
const LIGHT_GREY: [number, number, number] = [241, 244, 246];
const DARK: [number, number, number] = [33, 37, 41];

const MARGIN = 14;

export type EvaluationPdfViewer = 'Employee' | 'Manager';

type SectionMode = 'show' | 'pending' | 'hide';

function formatStatus(status: string): string {
  switch (status) {
    case 'NotStarted': return 'Not started';
    case 'EmployeeDraft': return 'Employee draft';
    case 'EmployeeSubmitted': return 'Submitted — awaiting manager review';
    case 'ManagerDraft': return 'Manager reviewing';
    case 'ManagerCompleted': return 'Manager review complete — awaiting acknowledgement';
    case 'Acknowledged': return 'Complete';
    default: return status;
  }
}

function formatCycleType(cycleType: string): string {
  switch (cycleType) {
    case 'YearEndEvaluation': return 'Year-End Evaluation';
    case 'ProbationReview': return 'Probation Review';
    case 'MidYearReview': return 'Mid-Year Review';
    default: return cycleType || 'Evaluation';
  }
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Same rules as MyEvaluationPage (employee) / EvaluationFormPage (manager sees everything). */
function sectionMode(section: SectionWithResponses, evaluation: EvaluationDetail, viewer: EvaluationPdfViewer): SectionMode {
  if (viewer === 'Manager') return 'show';

  const isMidYear = evaluation.cycleType === 'MidYearReview';
  const showManagerSections = evaluation.status === 'ManagerCompleted' || evaluation.status === 'Acknowledged';
  const isEmployeeSection = section.respondent === 'Employee';
  const isManagerSection = section.respondent === 'Manager';

  if (isMidYear) {
    return (showManagerSections || evaluation.status === 'Acknowledged') ? 'show' : 'hide';
  }
  if (isEmployeeSection) return 'show';
  if (isManagerSection) return showManagerSections ? 'show' : 'pending';
  return 'hide';
}

function canShowOverallRating(evaluation: EvaluationDetail, viewer: EvaluationPdfViewer): boolean {
  if (viewer === 'Manager') return true;
  return evaluation.status === 'ManagerCompleted' || evaluation.status === 'Acknowledged';
}

function ratingText(q: QuestionWithResponse): string {
  if (!q.selectedRatingLabel) return 'Not yet rated';
  return q.selectedRatingValue != null
    ? `${q.selectedRatingLabel} (${q.selectedRatingValue})`
    : q.selectedRatingLabel;
}

// ─── Header / footer ───

function addHeader(doc: jsPDF, evaluation: EvaluationDetail) {
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageW, 28, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(0, 28, pageW, 2, 'F');

  doc.setTextColor(...WHITE);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Origin — Orchestra Suite', MARGIN, 10);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCycleType(evaluation.cycleType), MARGIN, 20);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(evaluation.cycleName || '', pageW - MARGIN, 10, { align: 'right' });
  doc.text(`Printed: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`, pageW - MARGIN, 17, { align: 'right' });
  doc.setFontSize(8);
  doc.text('OQ Trading', pageW - MARGIN, 23, { align: 'right' });
}

function addFooters(doc: jsPDF, evaluation: EvaluationDetail) {
  const pageCount = doc.getNumberOfPages();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, pageH - 14, pageW - MARGIN, pageH - 14);

    doc.setFontSize(8);
    doc.setTextColor(...MID_GREY);
    doc.setFont('helvetica', 'normal');
    doc.text(`Confidential — ${evaluation.employeeName}`, MARGIN, pageH - 8);
    doc.text(`Page ${i} of ${pageCount}`, pageW - MARGIN, pageH - 8, { align: 'right' });
  }
}

function lastY(doc: jsPDF, fallback: number): number {
  return (doc as any).lastAutoTable?.finalY ?? fallback;
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > doc.internal.pageSize.getHeight() - 20) {
    doc.addPage();
    return 20;
  }
  return y;
}

// ─── Main ───

export function generateEvaluationPdf(evaluation: EvaluationDetail, viewer: EvaluationPdfViewer) {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const contentW = pageW - MARGIN * 2;

  addHeader(doc, evaluation);
  let y = 38;

  // Employee name + status line
  doc.setTextColor(...NAVY);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(evaluation.employeeName, MARGIN, y);
  y += 6;

  const isFinal = evaluation.status === 'Acknowledged';
  doc.setFontSize(9);
  doc.setFont('helvetica', isFinal ? 'normal' : 'bold');
  doc.setTextColor(...(isFinal ? MID_GREY : ORANGE));
  doc.text(
    isFinal ? `Status: ${formatStatus(evaluation.status)}` : `Status: ${formatStatus(evaluation.status)} — not finalised`,
    MARGIN, y
  );
  y += 6;

  // ─── Employee information ───
  const showOverall = canShowOverallRating(evaluation, viewer) && !!evaluation.overallRating;
  const infoRows: [string, string, string, string][] = [
    ['Employee', evaluation.employeeName || '—', 'Employee No.', evaluation.employeeNumber || '—'],
    ['Job Title', evaluation.employeeJobTitle || '—', 'Department', evaluation.department || '—'],
    ['Office', evaluation.office || '—', 'Manager', evaluation.managerName || '—'],
    ['Review Year', String(evaluation.reviewYear ?? '—'), 'Cycle', evaluation.cycleName || '—'],
    ['Employee Submitted', formatDate(evaluation.employeeSubmittedAt), 'Manager Completed', formatDate(evaluation.managerCompletedAt)],
    ['Acknowledged', formatDate(evaluation.employeeAcknowledgedAt), 'Overall Rating', showOverall ? (evaluation.overallRating as string) : '—'],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    body: infoRows,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2.2, textColor: DARK, lineColor: [226, 231, 236], lineWidth: 0.2 },
    columnStyles: {
      0: { fontStyle: 'bold', fillColor: LIGHT_GREY, cellWidth: 34 },
      1: { cellWidth: contentW / 2 - 34 },
      2: { fontStyle: 'bold', fillColor: LIGHT_GREY, cellWidth: 34 },
      3: { cellWidth: contentW / 2 - 34 },
    },
  });
  y = lastY(doc, y) + 8;

  // ─── Rating definitions (primary scale) ───
  const allOptions = evaluation.sections
    .flatMap(s => s.questions)
    .filter(q => q.ratingOptions && q.ratingOptions.length > 0)
    .map(q => q.ratingOptions!);
  const primaryScale = allOptions.length > 0 ? allOptions.reduce((a, b) => (a.length >= b.length ? a : b)) : [];
  const defs = [...primaryScale].filter(o => o.description).sort((a, b) => b.numericValue - a.numericValue);

  if (defs.length > 0) {
    y = ensureSpace(doc, y, 30);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text('Rating Definitions', MARGIN, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      margin: { left: MARGIN, right: MARGIN },
      head: [['Rating', 'Definition']],
      body: defs.map(o => [`${o.label} (${o.numericValue})`, o.description || '']),
      theme: 'grid',
      headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8.5 },
      styles: { fontSize: 8, cellPadding: 2, textColor: DARK, lineColor: [226, 231, 236], lineWidth: 0.2 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 42 } },
    });
    y = lastY(doc, y) + 8;
  }

  // ─── Sections ───
  const isMidYearWaiting = viewer === 'Employee'
    && evaluation.cycleType === 'MidYearReview'
    && (evaluation.status === 'NotStarted' || evaluation.status === 'ManagerDraft');

  if (isMidYearWaiting) {
    y = ensureSpace(doc, y, 14);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...MID_GREY);
    doc.text('Your manager is preparing this review. It will be available once they have completed it.', MARGIN, y);
    y += 8;
  } else {
    const sections = evaluation.sections
      .filter(s => !s.isSystem)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    for (const section of sections) {
      const mode = sectionMode(section, evaluation, viewer);
      if (mode === 'hide') continue;

      const isManagerSection = section.respondent === 'Manager';
      const accent = isManagerSection ? ORANGE : TURQUOISE;
      const respondentLabel = isManagerSection ? 'Manager review' : section.respondent === 'Employee' ? 'Employee self-assessment' : '';

      // Row metadata drives styling in didParseCell
      type RowKind = 'desc' | 'question' | 'qdesc' | 'rating' | 'comment' | 'pending';
      const kinds: RowKind[] = [];
      const body: string[][] = [];

      if (section.description) { body.push([section.description]); kinds.push('desc'); }

      if (mode === 'pending') {
        body.push(['Pending manager review']); kinds.push('pending');
      } else {
        const questions = [...section.questions].sort((a, b) => a.sortOrder - b.sortOrder);
        for (const q of questions) {
          body.push([q.title + (q.isOverallRating ? '  (Overall rating)' : '')]); kinds.push('question');
          if (q.description) { body.push([q.description]); kinds.push('qdesc'); }
          if (q.questionType === 'Rating') { body.push([`Rating: ${ratingText(q)}`]); kinds.push('rating'); }
          if (q.questionType === 'FreeText' || q.hasCommentField) {
            const c = (q.comment || '').trim();
            body.push([c.length > 0 ? c : 'No comment provided']); kinds.push('comment');
          }
        }
      }

      y = ensureSpace(doc, y, 24);
      autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN },
        head: [[respondentLabel ? `${section.title}   —   ${respondentLabel}` : section.title]],
        body,
        theme: 'plain',
        showHead: 'everyPage',
        rowPageBreak: 'auto',
        headStyles: { fillColor: accent, textColor: WHITE, fontStyle: 'bold', fontSize: 10.5, cellPadding: 3 },
        styles: { fontSize: 9, cellPadding: { top: 1.6, bottom: 1.6, left: 3, right: 3 }, textColor: DARK, overflow: 'linebreak' },
        didParseCell: (data: any) => {
          if (data.section !== 'body') return;
          const kind = kinds[data.row.index];
          switch (kind) {
            case 'desc':
              data.cell.styles.fontStyle = 'italic';
              data.cell.styles.textColor = MID_GREY;
              data.cell.styles.fontSize = 8.5;
              break;
            case 'question':
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = LIGHT_GREY;
              data.cell.styles.textColor = NAVY;
              data.cell.styles.cellPadding = { top: 2.4, bottom: 2.4, left: 3, right: 3 };
              break;
            case 'qdesc':
              data.cell.styles.textColor = MID_GREY;
              data.cell.styles.fontSize = 8;
              break;
            case 'rating':
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = data.cell.raw === 'Rating: Not yet rated' ? MID_GREY : accent;
              break;
            case 'comment':
              if (data.cell.raw === 'No comment provided') {
                data.cell.styles.fontStyle = 'italic';
                data.cell.styles.textColor = MID_GREY;
              }
              data.cell.styles.cellPadding = { top: 1.6, bottom: 3.5, left: 3, right: 3 };
              break;
            case 'pending':
              data.cell.styles.fontStyle = 'italic';
              data.cell.styles.textColor = MID_GREY;
              data.cell.styles.cellPadding = { top: 4, bottom: 4, left: 3, right: 3 };
              break;
          }
        },
        // Accent bar down the left edge of the section, like the on-screen card
        didDrawCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 0) {
            doc.setFillColor(...accent);
            doc.rect(data.cell.x, data.cell.y, 1.2, data.cell.height, 'F');
          }
        },
      });
      y = lastY(doc, y) + 8;
    }
  }

  // ─── Sign-off ───
  y = ensureSpace(doc, y, 34);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text('Sign-off', MARGIN, y);
  y += 3;

  autoTable(doc, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [['', 'Name', 'Date']],
    body: [
      ['Employee submitted', evaluation.employeeName || '—', formatDate(evaluation.employeeSubmittedAt)],
      ['Manager completed', evaluation.managerName || '—', formatDate(evaluation.managerCompletedAt)],
      ['Employee acknowledged', evaluation.employeeName || '—', formatDate(evaluation.employeeAcknowledgedAt)],
    ],
    theme: 'grid',
    headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 8.5 },
    styles: { fontSize: 9, cellPadding: 2.2, textColor: DARK, lineColor: [226, 231, 236], lineWidth: 0.2 },
    columnStyles: { 0: { fontStyle: 'bold', fillColor: LIGHT_GREY, cellWidth: 48 } },
  });

  addFooters(doc, evaluation);

  const safe = (s: string) => (s || '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  doc.save(`Evaluation_${safe(evaluation.employeeName)}_${safe(evaluation.cycleName)}.pdf`);
}
